import type { AbilityDef } from "../../sim";

/** Базовые удары: общие для всех героев. Кадры — старт, активные, восстановление; урон — из 1000 здоровья. */
const SPARK = { hit: "impacts/hit-spark", block: "block/spark" } as const;
const HEAVY = { hit: "heavy/spark", block: "block/spark" } as const;

export const JAB: AbilityDef = {
  id: "jab",
  name: "Джеб",
  input: { button: "lightPunch" },
  pose: "jab",
  startup: 4,
  active: 2,
  recovery: 7,
  advance: 1.5,
  next: "cross",
  hitbox: { x: 22, y: -132, w: 52, h: 26 },
  hit: { damage: 30, hitstun: 12, blockstun: 8, hitstop: 6, push: 5, height: "high" },
  fx: SPARK,
  sound: { hit: "hit-light" },
};

export const HOOK: AbilityDef = {
  id: "hook",
  name: "Хук",
  input: { button: "heavyPunch" },
  pose: "hook",
  startup: 8,
  active: 4,
  recovery: 16,
  advance: 2,
  hitbox: { x: 20, y: -136, w: 46, h: 36 },
  hit: { damage: 75, hitstun: 18, blockstun: 12, hitstop: 10, push: 8, height: "mid" },
  fx: HEAVY,
  sound: { hit: "hit-heavy" },
};

export const LOW_KICK: AbilityDef = {
  id: "light-kick",
  name: "Боковой ногой в корпус",
  input: { button: "lightKick" },
  pose: "lightKick",
  startup: 6,
  active: 3,
  recovery: 11,
  advance: 1,
  next: "front-kick",
  hitbox: { x: 20, y: -112, w: 66, h: 30 },
  hit: { damage: 45, hitstun: 15, blockstun: 9, hitstop: 7, push: 7, height: "mid" },
  fx: SPARK,
  sound: { hit: "hit-light" },
};

export const ROUNDHOUSE: AbilityDef = {
  id: "heavy-kick",
  name: "Высокий ногой в голову",
  input: { button: "heavyKick" },
  pose: "heavyKick",
  startup: 12,
  active: 4,
  recovery: 20,
  advance: 2.5,
  next: "spin-kick",
  hitbox: { x: 24, y: -158, w: 70, h: 36 },
  hit: { damage: 95, hitstun: 22, blockstun: 14, hitstop: 11, push: 7, height: "high" },
  fx: HEAVY,
  sound: { hit: "hit-heavy" },
};

export const UPPERCUT: AbilityDef = {
  id: "uppercut",
  name: "Апперкот",
  input: { button: "heavyPunch", hold: "D" },
  pose: "uppercut",
  startup: 6,
  active: 5,
  recovery: 26,
  hitbox: { x: 16, y: -172, w: 52, h: 104 },
  hit: { damage: 95, hitstun: 0, blockstun: 16, hitstop: 13, push: 4, height: "mid", launch: 20 },
  fx: { hit: "heavy/spark", block: "block/spark" },
  sound: { hit: "hit-heavy" },
};

export const CROUCH_JAB: AbilityDef = {
  id: "crouch-jab",
  name: "Тычок снизу",
  input: { button: "lightPunch", hold: "D" },
  pose: "crouchJab",
  startup: 4,
  active: 3,
  recovery: 9,
  hitbox: { x: 24, y: -94, w: 50, h: 24 },
  hit: { damage: 25, hitstun: 11, blockstun: 8, hitstop: 5, push: 4, height: "low" },
  fx: SPARK,
  sound: { hit: "hit-light" },
};

/** ↓ + ✕ — низкий удар по ногам с разворота (Leg Sweep): из приседа у каждой кнопки свой удар (Джон 04.11.2026). */
export const CROUCH_KICK: AbilityDef = {
  id: "crouch-kick",
  name: "Низкий по ногам",
  input: { button: "lightKick", hold: "D" },
  pose: "legSweep",
  startup: 8,
  active: 4,
  recovery: 14,
  advance: 1,
  hitbox: { x: 8, y: -38, w: 92, h: 38 },
  hit: { damage: 45, hitstun: 16, blockstun: 9, hitstop: 7, push: 5, height: "low" },
  fx: SPARK,
  sound: { hit: "hit-light" },
};

/** → + □ вплотную — локоть (Illegal Elbow Punch; Джон 04.11.2026, 22:29): короткий, сильный, вдавливает назад. */
export const CLOSE_ELBOW: AbilityDef = {
  id: "close-elbow",
  name: "Локоть вплотную",
  input: { button: "lightPunch", hold: "F", near: 95 },
  pose: "overhandElbow",
  startup: 6,
  active: 3,
  recovery: 14,
  advance: 1,
  hitbox: { x: 14, y: -160, w: 56, h: 60 },
  hit: { damage: 60, hitstun: 18, blockstun: 11, hitstop: 9, push: 9, height: "high" },
  fx: HEAVY,
  sound: { hit: "hit-heavy" },
};

export const SWEEP: AbilityDef = {
  id: "sweep",
  name: "Подсечка",
  input: { button: "heavyKick", hold: "D" },
  pose: "sweep",
  startup: 9,
  active: 9,
  recovery: 18,
  actions: [{ frame: 4, type: "dash", params: { speed: 9 } }],
  hitbox: { x: 8, y: -30, w: 84, h: 30 },
  hit: { damage: 60, hitstun: 0, blockstun: 12, hitstop: 9, push: 4, height: "low", knockdown: true, launch: 4 },
  fx: HEAVY,
  sound: { hit: "hit-heavy" },
};

export const AIR_KICK: AbilityDef = {
  id: "air-kick",
  name: "Удар в прыжке",
  input: { button: "lightKick", air: true },
  pose: "airKick",
  startup: 4,
  active: 9,
  recovery: 6,
  hitbox: { x: 20, y: -80, w: 56, h: 36 },
  hit: { damage: 55, hitstun: 16, blockstun: 10, hitstop: 8, push: 6, height: "overhead" },
  fx: SPARK,
  sound: { hit: "hit-light" },
};

export const AIR_HEAVY: AbilityDef = {
  id: "air-heavy",
  name: "Пятка в прыжке",
  input: { button: "heavyKick", air: true },
  pose: "airHeavy",
  startup: 6,
  active: 6,
  recovery: 8,
  hitbox: { x: 16, y: -72, w: 68, h: 44 },
  hit: { damage: 70, hitstun: 18, blockstun: 12, hitstop: 10, push: 8, height: "overhead" },
  fx: HEAVY,
  sound: { hit: "hit-heavy" },
};

/** Авто-серия □ □ □ □ (Джон 04.11.2026: «лёгкие комбо… проще чем в мортал комбат»): джеб → кросс → хук → добивание. */
export const CROSS: AbilityDef = {
  id: "cross",
  name: "Кросс",
  input: { button: "lightPunch", series: true },
  pose: "cross",
  startup: 5,
  active: 3,
  recovery: 9,
  advance: 2.5,
  next: "body-hook",
  hitbox: { x: 22, y: -134, w: 50, h: 26 },
  hit: { damage: 35, hitstun: 14, blockstun: 9, hitstop: 6, push: 4, height: "high" },
  fx: SPARK,
  sound: { hit: "hit-light" },
};

export const BODY_HOOK: AbilityDef = {
  id: "body-hook",
  name: "Хук",
  input: { button: "lightPunch", series: true },
  pose: "bodyHook",
  startup: 6,
  active: 3,
  recovery: 10,
  advance: 2,
  next: "series-finisher",
  hitbox: { x: 20, y: -130, w: 48, h: 34 },
  hit: { damage: 40, hitstun: 16, blockstun: 10, hitstop: 7, push: 4, height: "mid" },
  fx: SPARK,
  sound: { hit: "hit-light" },
};

/** Конец серии — добивающий апперкот: подбрасывает, волна частиц, дальше бой в воздухе. */
export const SERIES_FINISHER: AbilityDef = {
  id: "series-finisher",
  name: "Добивание",
  input: { button: "lightPunch", series: true },
  pose: "finisher",
  startup: 7,
  active: 5,
  recovery: 22,
  advance: 3,
  hitbox: { x: 14, y: -176, w: 54, h: 110 },
  hit: { damage: 80, hitstun: 0, blockstun: 16, hitstop: 12, push: 6, height: "mid", launch: 17 },
  fx: HEAVY,
  sound: { start: "whoosh", hit: "hit-heavy" },
};

/** Авто-серия ✕ ✕ ✕: боковой в корпус → прямой ногой → вертушка в прыжке с подбросом. */
export const FRONT_KICK: AbilityDef = {
  id: "front-kick",
  name: "Прямой ногой",
  input: { button: "lightKick", series: true },
  pose: "frontKick",
  startup: 6,
  active: 4,
  recovery: 11,
  advance: 2.5,
  next: "hurricane",
  hitbox: { x: 20, y: -120, w: 72, h: 32 },
  hit: { damage: 50, hitstun: 16, blockstun: 10, hitstop: 7, push: 5, height: "mid" },
  fx: SPARK,
  sound: { hit: "hit-light" },
};

export const HURRICANE: AbilityDef = {
  id: "hurricane",
  name: "Вертушка",
  input: { button: "lightKick", series: true },
  pose: "hurricane",
  startup: 7,
  active: 10,
  recovery: 18,
  advance: 3,
  hitbox: { x: 0, y: -150, w: 100, h: 70 },
  hit: { damage: 85, hitstun: 0, blockstun: 16, hitstop: 12, push: 9, height: "mid", launch: 15 },
  fx: HEAVY,
  sound: { start: "whoosh", hit: "hit-heavy" },
};

/** Серия ○ ○: высокий в голову → разворот с ударом ногой, сбивает с ног. */
export const SPIN_KICK: AbilityDef = {
  id: "spin-kick",
  name: "Разворот ногой",
  input: { button: "heavyKick", series: true },
  pose: "spinKick",
  startup: 9,
  active: 5,
  recovery: 20,
  advance: 5,
  hitbox: { x: 20, y: -160, w: 70, h: 40 },
  hit: { damage: 90, hitstun: 0, blockstun: 16, hitstop: 12, push: 14, height: "high", knockdown: true, launch: 7 },
  fx: HEAVY,
  sound: { start: "whoosh", hit: "hit-heavy" },
};

/** → + ✕ — прямой ногой с шагом вперёд: дальше бокового, отталкивает. */
export const STEP_KICK: AbilityDef = {
  id: "step-kick",
  name: "Прямой ногой с шагом",
  input: { button: "lightKick", hold: "F" },
  pose: "frontKick",
  startup: 7,
  active: 4,
  recovery: 13,
  advance: 5,
  hitbox: { x: 20, y: -120, w: 76, h: 32 },
  hit: { damage: 55, hitstun: 16, blockstun: 10, hitstop: 8, push: 9, height: "mid" },
  fx: SPARK,
  sound: { hit: "hit-light" },
};

/** □ в прыжке — кулак сверху: быстрый удар в воздухе, бьёт через блок стоя только сверху. */
export const AIR_PUNCH: AbilityDef = {
  id: "air-punch",
  name: "Кулак в прыжке",
  input: { button: "lightPunch", air: true },
  pose: "airPunch",
  startup: 3,
  active: 7,
  recovery: 5,
  hitbox: { x: 16, y: -96, w: 54, h: 56 },
  hit: { damage: 40, hitstun: 14, blockstun: 9, hitstop: 6, push: 5, height: "overhead" },
  fx: SPARK,
  sound: { hit: "hit-light" },
};

/** Атака с пола: сбитый, нажав удар при падении, бьёт ногами снизу и встаёт выпадом — сразу в бой. */
export const RISE_KICK: AbilityDef = {
  id: "rise-kick",
  name: "Атака с пола (удар при падении)",
  input: { button: "heavyKick", series: true },
  pose: "riseKick",
  startup: 9,
  active: 6,
  recovery: 12,
  advance: 3,
  hitbox: { x: 6, y: -96, w: 84, h: 70 },
  hit: { damage: 60, hitstun: 0, blockstun: 12, hitstop: 9, push: 6, height: "mid", launch: 10 },
  fx: HEAVY,
  sound: { start: "whoosh", hit: "hit-heavy" },
};

/** ← + □ — локоть с глубоким выпадом: короткий, сильный, далеко доходит. */
export const ELBOW_LUNGE: AbilityDef = {
  id: "elbow-lunge",
  name: "Локоть с выпадом",
  input: { button: "lightPunch", hold: "B" },
  pose: "elbowLunge",
  startup: 6,
  active: 3,
  recovery: 12,
  advance: 5,
  hitbox: { x: 20, y: -130, w: 60, h: 30 },
  hit: { damage: 55, hitstun: 17, blockstun: 10, hitstop: 8, push: 8, height: "mid" },
  fx: SPARK,
  sound: { hit: "hit-heavy" },
};

/** ← + △ — локоть снизу вверх: сбивает прыгающего и чуть подбрасывает. */
export const RISING_ELBOW: AbilityDef = {
  id: "rising-elbow",
  name: "Локоть снизу",
  // ← + △ отдан магическому выстрелу (Джон 04.11.2026, 22:29); локоть снизу — ветка серии «вниз»: ↓ □ □ △
  input: { button: "heavyPunch", series: true },
  pose: "risingElbow",
  startup: 7,
  active: 4,
  recovery: 16,
  hitbox: { x: 14, y: -170, w: 50, h: 90 },
  hit: { damage: 70, hitstun: 0, blockstun: 12, hitstop: 10, push: 4, height: "mid", launch: 11 },
  fx: HEAVY,
  sound: { hit: "hit-heavy" },
};

/** → + ○ — дропкик: прыжок вперёд, удар двумя ногами, сбивает с ног. */
export const DROP_KICK: AbilityDef = {
  id: "drop-kick",
  name: "Дропкик",
  input: { button: "heavyKick", hold: "F" },
  pose: "dropKick",
  startup: 10,
  active: 8,
  recovery: 14,
  actions: [{ frame: 3, type: "leap", params: { up: 9, forward: 8 } }],
  hitbox: { x: 10, y: -110, w: 80, h: 50 },
  hit: { damage: 90, hitstun: 0, blockstun: 14, hitstop: 11, push: 12, height: "mid", knockdown: true, launch: 6 },
  fx: HEAVY,
  sound: { start: "whoosh", hit: "hit-heavy" },
};

/** Зажать □ на полсекунды и отпустить — заряженный прямой: рывок вперёд, сбивает с ног, волна частиц. */
export const CHARGED_PUNCH: AbilityDef = {
  id: "charged-punch",
  name: "Заряженный прямой",
  input: { button: "lightPunch", charge: 30 },
  pose: "chargePunch",
  startup: 6,
  active: 5,
  recovery: 20,
  advance: 7,
  hitbox: { x: 22, y: -136, w: 56, h: 30 },
  hit: {
    damage: 180,
    hitstun: 0,
    blockstun: 18,
    hitstop: 12,
    push: 14,
    height: "high",
    knockdown: true,
    launch: 6,
    chip: 10,
  },
  fx: { start: "particles/burst", hit: "heavy/spark", block: "block/spark" },
  sound: { start: "whoosh", hit: "hit-heavy" },
};

/** Зажать ○ на полсекунды и отпустить — заряженный удар ногой в голову: подбрасывает, дальше бой в воздухе. */
export const CHARGED_KICK: AbilityDef = {
  id: "charged-kick",
  name: "Заряженный хай-кик",
  input: { button: "heavyKick", charge: 30 },
  pose: "chargeKick",
  startup: 9,
  active: 5,
  recovery: 22,
  advance: 9,
  hitbox: { x: 24, y: -160, w: 74, h: 40 },
  hit: { damage: 190, hitstun: 0, blockstun: 18, hitstop: 12, push: 10, height: "high", launch: 15, chip: 12 },
  fx: { start: "particles/burst", hit: "heavy/spark", block: "block/spark" },
  sound: { start: "whoosh", hit: "hit-heavy" },
};

/** С бега ✕ — подкат ногами вперёд: низкий, сбивает с ног, разгон бега несёт дальше. */
export const RUN_SLIDE: AbilityDef = {
  id: "run-slide",
  name: "Подкат с бега",
  input: { button: "lightKick", run: true },
  pose: "slide",
  startup: 5,
  active: 14,
  recovery: 14,
  actions: [{ frame: 0, type: "dash", params: { speed: 12 } }],
  hitbox: { x: 8, y: -30, w: 88, h: 30 },
  hit: { damage: 70, hitstun: 0, blockstun: 12, hitstop: 8, push: 5, height: "low", knockdown: true, launch: 5 },
  fx: HEAVY,
  sound: { start: "whoosh", hit: "hit-heavy" },
};

/** С бега ○ — колено в прыжке с разгона: подбрасывает, дальше — бой в воздухе. */
export const RUN_KNEE: AbilityDef = {
  id: "run-knee",
  name: "Колено с разбега",
  input: { button: "heavyKick", run: true },
  pose: "runKnee",
  startup: 7,
  active: 8,
  recovery: 12,
  actions: [{ frame: 2, type: "leap", params: { up: 9, forward: 9 } }],
  // колено в кадр удара клипа — ~69 px над стопами (обмер 05.11.2026); зона удара на его высоте
  hitbox: { x: 14, y: -94, w: 54, h: 50 },
  hit: { damage: 85, hitstun: 0, blockstun: 14, hitstop: 10, push: 6, height: "mid", launch: 13 },
  fx: HEAVY,
  sound: { start: "whoosh", hit: "hit-heavy" },
};

export const BASE_KIT: readonly AbilityDef[] = [
  JAB,
  HOOK,
  LOW_KICK,
  ROUNDHOUSE,
  UPPERCUT,
  CROUCH_JAB,
  CROUCH_KICK,
  CLOSE_ELBOW,
  SWEEP,
  AIR_KICK,
  AIR_HEAVY,
  RUN_SLIDE,
  RUN_KNEE,
  CROSS,
  BODY_HOOK,
  SERIES_FINISHER,
  CHARGED_PUNCH,
  CHARGED_KICK,
  FRONT_KICK,
  HURRICANE,
  SPIN_KICK,
  STEP_KICK,
  AIR_PUNCH,
  RISE_KICK,
  ELBOW_LUNGE,
  RISING_ELBOW,
  DROP_KICK,
];
